import { toast as sonnerToast } from "sonner";
import { recordToast, type Severity } from "./toastHistory";

/**
 * Drop-in replacement for `import { toast } from "sonner"` that records
 * each toast to the persistent history (lib/toastHistory.ts) before
 * delegating to Sonner. Use everywhere in the app instead of importing
 * sonner directly — that way every pop-up is reviewable later via the
 * floating notifications bell.
 *
 * Only the methods actually used in the codebase are wrapped: success,
 * warning, error, info. If you need promise/loading/dismiss/etc., add
 * the passthrough here so the recording stays consistent.
 */

type SonnerMethod = (typeof sonnerToast)["success"];
type ToastMessage = Parameters<SonnerMethod>[0];
type ToastOptions = Parameters<SonnerMethod>[1];

function messageToString(message: ToastMessage): string {
  if (typeof message === "string") return message;
  // Sonner accepts ReactNode messages too — record as best-effort string.
  return String(message ?? "");
}

function descriptionFromOptions(options: ToastOptions): string | undefined {
  if (!options || typeof options !== "object") return undefined;
  const desc = (options as { description?: unknown }).description;
  return typeof desc === "string" ? desc : undefined;
}

function wrap(severity: Severity) {
  const fn = sonnerToast[severity];
  return (message: ToastMessage, options?: ToastOptions) => {
    recordToast(severity, messageToString(message), descriptionFromOptions(options));
    return fn(message, options);
  };
}

export const toast = {
  success: wrap("success"),
  warning: wrap("warning"),
  error: wrap("error"),
  info: wrap("info"),
  // Progress / in-flight indicator — not recorded to history because
  // it's typically updated several times under the same id (e.g.
  // "Downloading 3/10…" → "Downloading 4/10…") and would spam the
  // panel. The eventual success/warning that replaces it IS recorded.
  loading: (message: ToastMessage, options?: ToastOptions) =>
    sonnerToast.loading(message, options),
  // Pass-through for promise-shaped toasts that need an explicit dismiss.
  dismiss: (id?: string | number) => sonnerToast.dismiss(id),
};
